import React, { useCallback, useState } from 'react'
import { useDispatch, useSelector } from 'react-redux'
import { useForm } from 'react-hook-form'
import * as yup from 'yup'
import { yupResolver } from '@hookform/resolvers/yup'
import Checkbox from '@groovehq/internal-design-system/lib/components/Checkbox/Checkbox'
import MessageCard from '@groovehq/internal-design-system/lib/components/MessageCard/MessageCard'
import AnimatedEllipsis from '@groovehq/internal-design-system/lib/components/AnimatedEllipsis/AnimatedEllipsis'
import Modal from '@groovehq/internal-design-system/lib/components/Modal/Modal'
import { doGmailImport } from 'ducks/mailboxes/actions'
import { selectIsInInbox, selectIsInSettings } from 'selectors/location'
import {
  useRedirectToChannel,
  useRebuildLeftNavMenu,
} from 'ducks/channels/hooks'
import { doClearChannelDraft } from 'ducks/channels/actions'
import {
  paragraph,
  text,
  divider,
} from '@groovehq/internal-design-system/lib/styles/elements'
import { add } from 'util/date'
import { useProductFeature } from 'ducks/billing/hooks'
import { INBOX } from 'ducks/billing/productTypes'
import { FEATURE_INBOX_MAX_SEARCH_HISTORY_DAYS } from 'ducks/billing/featureTypes'
import { snow } from 'util/ui/confetti'
import { selectCurrentMailboxId } from 'selectors/mailboxes/selectCurrentMailboxId'
import { selectIsTestAccount } from 'selectors/account'
import debug from 'util/debug'
import { styles } from './styles'
import ImportPeriodDropdown from './ImportPeriodDropdown'

const FORM_SCHEMA = yup.object().shape({
  import_labels: yup.boolean(),
  close_tickets: yup.boolean(),
  stop_importing_at: yup.number().nullable(),
})

const formEmptyState = {
  import_labels: true,
  close_tickets: false,
  stop_importing_at: 1,
}

const formatTimePeriod = days => {
  if (days < 30) {
    // Output in days
    return `${days} day${days > 1 ? 's' : ''}`
  }
  // Convert days to months and output
  const months = Math.round(days / 30) // You can change rounding method as needed
  return `${months} month${months > 1 ? 's' : ''}`
}

const MailImport = ({
  onClose,
  onExit,
  drawerResourceId: channelId,
  drawerId,
  previousDrawer,
}) => {
  const [isSaving, setIsSaving] = useState(null)
  const [errors, setErrors] = useState(null)

  const dispatch = useDispatch()
  const isInInbox = useSelector(selectIsInInbox)
  const isInSettings = useSelector(selectIsInSettings)
  const currentMailboxId = useSelector(selectCurrentMailboxId)
  const isTestAccount = useSelector(selectIsTestAccount)

  const { exitAndRedirect } = useRebuildLeftNavMenu({
    channelId, // channel id after mailbox is created
    onExit,
  })

  const { rebuildExitAndRedirect } = useRedirectToChannel({
    channelId, // channel id after mailbox is created
    onExit,
  })

  const useFormMethods = useForm({
    mode: 'all',
    resolver: yupResolver(FORM_SCHEMA),
    defaultValues: formEmptyState,
  })

  const {
    register,
    handleSubmit,
    reset,
    control,
    formState: { isValid },
  } = useFormMethods

  const handleOnExit = useCallback(
    () => {
      reset(formEmptyState)
      dispatch(doClearChannelDraft(channelId))
      onExit()
    },
    [onExit, reset, channelId, dispatch]
  )

  const onSubmit = useCallback(
    async data => {
      try {
        setIsSaving(true)
        setErrors(null)
        const time = new Date(Date.now())
        const stopImportingAt = data.stop_importing_at
        if (!isTestAccount) {
          await dispatch(
            doGmailImport(channelId, {
              ...data,
              pages_to_import: null,
              stop_importing_at: stopImportingAt
                ? add(time, -stopImportingAt, 'day').getTime()
                : null,
            })
          )
        }
        dispatch(doClearChannelDraft(channelId))
        if (isInInbox || isInSettings) {
          rebuildExitAndRedirect()

          if (currentMailboxId === channelId) {
            snow({ duration: 1 })
          }
        } else {
          exitAndRedirect()
        }
        setIsSaving(false)
      } catch (error) {
        setIsSaving(false)
        setErrors(error)
        debug('Unable to start import', { error })
      }
    },
    [
      dispatch,
      channelId,
      isInInbox,
      isInSettings,
      rebuildExitAndRedirect,
      exitAndRedirect,
      currentMailboxId,
      isTestAccount,
    ]
  )

  const handleBack = useCallback(
    () => {
      onClose({ ignoreStack: false })
    },
    [onClose]
  )

  const { featureLimit } = useProductFeature(
    INBOX,
    FEATURE_INBOX_MAX_SEARCH_HISTORY_DAYS
  )

  const formId = `${drawerId}-form`

  return (
    <Modal
      title="Import existing emails"
      onClose={handleOnExit}
      data-test-id="channels-mail-import"
      portal="#drawer-root"
      open
    >
      <Modal.Title>Import</Modal.Title>
      {!previousDrawer && (
        <Modal.Description>
          Don{"'"}t worry, existing conversations won{"'"}t be changed. Only
          missing conversations will be imported.
        </Modal.Description>
      )}
      <div className="grui pt-10 pb-6">
        <form id={formId} onSubmit={handleSubmit(onSubmit)}>
          <p
            css={[
              paragraph.styles.preflight,
              text.styles.textNormal,
              styles.container,
            ]}
          >
            <p className="grui mb-4">How much history should be imported?</p>
            <div
              css={divider.styles.primary}
              role="separator"
              className="grui mb-8"
            />
            <ImportPeriodDropdown control={control} name="stop_importing_at" />
          </p>
          <p
            css={[paragraph.styles.preflight, text.styles.textXs]}
            className="grui mt-8"
          >
            <Checkbox id="import_labels" {...register('import_labels')}>
              Import labels as tags in Groove
            </Checkbox>
            <div
              css={divider.styles.primary}
              role="separator"
              className="grui mt-8 mb-8"
            />
          </p>

          {featureLimit !== 'unlimited' && (
            <p className="grui mt-8">
              <MessageCard
                css={styles.container}
                type="warning"
                className="grui mb-12"
                visible
              >
                Your actual history will be limited to{' '}
                {formatTimePeriod(featureLimit)} until you upgrade your plan.
              </MessageCard>
            </p>
          )}
          <p className="grui mt-8">
            <MessageCard
              css={styles.container}
              type="info"
              className="grui mb-12"
              visible
            >
              The import will run in the background. We{"'"}ll let you know when
              this finishes.
            </MessageCard>
            <MessageCard
              type="negative"
              className="grui mb-12"
              visible={!!errors}
            >
              An error occurred importing your conversations. Please try again.
            </MessageCard>
          </p>
          <p className="grui mt-10" />
        </form>
        {previousDrawer === 'channels-members-edit' && (
          <Modal.Button
            type="tertiary"
            onClick={handleBack}
            className="grui mt-8"
          >
            Back
          </Modal.Button>
        )}
        <Modal.Button
          type="info"
          disabled={isSaving || !isValid}
          onClick={handleSubmit(onSubmit)}
          className="grui mt-8"
          data-test-id="finish-and-redirect-inbox"
        >
          <>
            {isSaving ? (
              <span>
                Saving<AnimatedEllipsis />
              </span>
            ) : (
              <span>Start import &amp; take me to my {app.t('mailbox')}</span>
            )}
          </>
        </Modal.Button>
      </div>
    </Modal>
  )
}

export default MailImport
